import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {COPILOT_ASSIGNMENT_API_ENDPOINT} from './constants'

export interface CopilotAssignmentOptions {
  instructions: string
  targetRepo: string
  targetBranch: string
}

export interface CopilotAssignmentJob {
  issue_number: number
  job: {
    id: string
  }
}

export interface CopilotAssignmentSuccessResponse {
  jobs: CopilotAssignmentJob[]
}

export interface CopilotAssignmentErrorResponse {
  error: string
}

export interface CopilotAssignmentResponse {
  ok: boolean
  status: number
  data?: CopilotAssignmentSuccessResponse
  errorMessage?: string
}

/**
 * Assigns an issue to Copilot with the specified instructions and target repository.
 *
 * @param owner - The repository owner
 * @param repo - The repository name
 * @param issueNumber - The issue number to assign
 * @param options - Assignment options including instructions and target repository
 * @returns Promise resolving to the API response
 */
export async function assignIssueToCopilot(
  owner: string,
  repo: string,
  issueNumber: number,
  options: CopilotAssignmentOptions,
): Promise<CopilotAssignmentResponse> {
  try {
    const response = await verifiedFetchJSON(`/${owner}/${repo}${COPILOT_ASSIGNMENT_API_ENDPOINT}`, {
      method: 'POST',
      body: {
        issue_ids: [issueNumber],
        repo_name_with_owner: options.targetRepo,
        base_ref: options.targetBranch,
        custom_instructions: options.instructions,
      },
    })

    if (response.ok) {
      const data: CopilotAssignmentSuccessResponse = await response.json()
      return {
        ok: true,
        status: response.status,
        data,
      }
    } else {
      // Try to extract error message from response body
      let errorMessage: string
      try {
        const errorData: CopilotAssignmentErrorResponse = await response.json()
        errorMessage = errorData.error || `Request failed with status ${response.status}`
      } catch {
        errorMessage = `Request failed with status ${response.status}`
      }

      return {
        ok: false,
        status: response.status,
        errorMessage,
      }
    }
  } catch (error) {
    return {
      ok: false,
      status: 0,
      errorMessage: error instanceof Error ? error.message : 'An unexpected error occurred',
    }
  }
}
